/**
 * Tests sorted into the groups a doctor or HR reads a battery in — vitals,
 * eyes, blood count, sugar, lipids, liver, kidney, urine and so on — instead
 * of one long list of codes. Decided by the test code, which the catalogue
 * names by convention (PAT-URINE-…, VIT-…, PHY-…); anything unrecognised
 * falls into "Other".
 */

export interface GroupedTests<T> { key: string; label: string; tests: T[] }

const GROUPS: Array<{ key: string; label: string; match: (code: string) => boolean }> = [
  { key: 'vitals', label: 'Body measurements & vitals', match: (c) => c.startsWith('VIT-') },
  { key: 'doctor', label: 'Doctor’s examination', match: (c) => c.startsWith('PHY-') },
  { key: 'eyes', label: 'Eyes', match: (c) => c.startsWith('VIS-') },
  { key: 'hearing', label: 'Hearing (audiometry)', match: (c) => c.startsWith('AUD-') },
  { key: 'cbc', label: 'Blood count (CBC)', match: (c) => /^PAT-(HB|WBC|PLT|RBC|HCT|MCV|MCH|MCHC|RDW|NEUT|LYMPH|EOS|MONO|BASO|ESR)$|-MORPH$/.test(c) },
  { key: 'sugar', label: 'Blood sugar', match: (c) => /^PAT-(FBS|PPBS|RBS|HBA1C)$/.test(c) },
  { key: 'lipid', label: 'Lipid profile', match: (c) => /^PAT-(CHOL|TG|HDL|LDL|VLDL)$/.test(c) },
  { key: 'liver', label: 'Liver (LFT)', match: (c) => /^PAT-(SGPT|SGOT|BIL-T|BIL-D|ALP|GGT|PROT|ALB)$/.test(c) },
  { key: 'kidney', label: 'Kidney (RFT)', match: (c) => /^PAT-(CREAT|UREA|UA)$/.test(c) },
  { key: 'thyroid', label: 'Thyroid', match: (c) => /^PAT-(TSH|T3|T4)/.test(c) },
  { key: 'serology', label: 'Blood group & infection', match: (c) => /^PAT-(BLOOD-GROUP|HBSAG|HCV|HIV|VDRL)/.test(c) },
  { key: 'urine', label: 'Urine routine', match: (c) => c.startsWith('PAT-URINE-') && !/HIPP|MHA|PHENOL|TTMA/.test(c) },
  { key: 'blood-other', label: 'Other blood tests', match: (c) => c.startsWith('PAT-') },
  { key: 'heart', label: 'Heart (ECG)', match: (c) => c.startsWith('ECG-') },
  { key: 'xray', label: 'X-ray', match: (c) => c.startsWith('RAD-') },
  { key: 'lungs', label: 'Lungs (spirometry)', match: (c) => c.startsWith('SPI-') },
  { key: 'dental', label: 'Dental', match: (c) => c.startsWith('DEN-') },
];

/** The tests in display groups, in a fixed order; empty groups are left out. */
export function groupTests<T extends { code: string }>(tests: readonly T[]): Array<GroupedTests<T>> {
  const out = new Map<string, GroupedTests<T>>();
  for (const g of GROUPS) out.set(g.key, { key: g.key, label: g.label, tests: [] });
  out.set('other', { key: 'other', label: 'Other', tests: [] });
  for (const t of tests) {
    const g = GROUPS.find((x) => x.match(t.code));
    out.get(g?.key ?? 'other')!.tests.push(t);
  }
  return [...out.values()].filter((g) => g.tests.length > 0);
}
